// Section. Sleeve name cross-sell.
//
// Renders ONLY when:
//   - The customer has at least one saved spot
//   - None of those spots sit on a sleeve placement
//   - The active template has a "sleeve_name" upsell wired up
//   - The template offers at least one sleeve placement slug
//
// The card shows the sleeve_name upsell label/price plus emoji chips for
// any "sleeve_emoji_*" upsells. Picking "Add to my order" spawns a new
// draft pre-filled with the sleeve placement + the ticked emojis as
// stackable extras on that draft. Customer fills in the name in the
// regular editor flow.

import * as React from "react";
import type { Config, Upsell } from "../types";
import type { EmbroideryDraft } from "./PlacementSection";
import type { Spot } from "../pricing";
import { emptyDraft } from "./PlacementSection";
import { sleeveCrossSellUpsellFor } from "../flow";
import {
  additionalPlacementFeeFor,
  effectiveTiersFor,
  tierForSize,
} from "../pricing";

// Placement slugs that count as "on the sleeve". Includes both
// long-form (`left-sleeve`) and short-form (`l-sleeve`) variants because
// the seeded placements use the short-form on this shop. Comparison
// is case-insensitive via lowercasing at lookup-time.
const SLEEVE_SLUGS = new Set([
  "left-sleeve",
  "right-sleeve",
  "sleeve",
  "l-sleeve",
  "r-sleeve",
  "left_sleeve",
  "right_sleeve",
]);

export function SleeveCrossSellSection({
  config,
  spots,
  draft,
  onPickSleeve,
}: {
  config: Config;
  spots: Spot[];
  draft: EmbroideryDraft | null;
  /** Spawns a new draft. Parent (App.tsx) sets it as the active draft
   *  and unmounts this section automatically once the new spot lands
   *  on a sleeve placement. */
  onPickSleeve: (next: EmbroideryDraft) => void;
}) {
  // Phase 33e + 42c / 70: pick which sleeve cross-sell to show. Kid
  // flow gets the dedicated "Add their name + age" copy variant; pet +
  // logo flows share the standard "Add a name on the sleeve" copy.
  // Heart / Paw emoji chips render on every flow now - customers asked
  // for the option regardless of which design path they're on.
  //
  // Phase 70: the sleeve upsell comes from the bound FlowBlueprint's
  // sleeveCrossSellUpsellId. The kid-spot detection still bumps to the
  // name+age variant when present, since some merchants will run the
  // standard kid blueprint without flipping its sleeve upsell.
  const kidUpsell = config.upsells?.find((u) => u.kind === "kid_drawing");
  const hasKidSpot = !!kidUpsell && spots.some(
    (s) => s.addonUpsellId === kidUpsell.id,
  );
  const sleeveUpsell = sleeveCrossSellUpsellFor(config, hasKidSpot);
  const emojiUpsells = (config.upsells ?? []).filter((u) =>
    u.kind.startsWith("sleeve_emoji_"),
  );
  // Phase 56: if the upsell declares an explicit appliesAtPlacementSlug
  // (admin-configured), honour that and find the matching placement.
  // Otherwise fall back to the SLEEVE_SLUGS heuristic for upsells from
  // before the field existed.
  const explicitSlug =
    (sleeveUpsell as { appliesAtPlacementSlug?: string | null } | undefined)
      ?.appliesAtPlacementSlug ?? null;
  const sleevePlacement = explicitSlug
    ? config.placements.find((p) => p.slug === explicitSlug)
    : config.placements.find((p) => SLEEVE_SLUGS.has(p.slug));

  // Gate: at least one saved spot, no spot already on the target
  // placement, and an open editor draft would be confusing — we hide
  // while the customer's mid-edit so the cross-sell doesn't compete with
  // the editor.
  const haveTargetSpot = explicitSlug
    ? spots.some((s) => s.placementSlug === explicitSlug)
    : spots.some((s) => s.placementSlug && SLEEVE_SLUGS.has(s.placementSlug));

  // Local: emojis the customer has ticked + the typed name. Cleared
  // when the cross-sell hands off to the editor.
  const [ticked, setTicked] = React.useState<Set<string>>(new Set());
  const [name, setName] = React.useState<string>("");

  // Phase 59: short-circuit when the resolved garment has no sleeves
  // (socks, hats, totes, beanies, bags). Doesn't matter whether an
  // appliesAtPlacementSlug is set on the upsell — if the GARMENT has
  // `hasSleeves: false` we never offer a sleeve cross-sell. The same
  // check guards against templates that accidentally inherit a sleeve
  // placement from a parent or master library that doesn't apply to
  // this product's actual shape.
  const garmentHasSleeves =
    (config.garmentType as { hasSleeves?: boolean }).hasSleeves !== false;
  if (!garmentHasSleeves && !explicitSlug) return null;

  if (!sleeveUpsell) return null;
  if (!sleevePlacement) return null;
  if (spots.length === 0) return null;
  if (haveTargetSpot) return null;
  if (draft !== null) return null;

  // 2026-07-25: the badge used to print the upsell's own priceCents ($19),
  // but nothing ever attached that upsell id to the spawned draft - the
  // spot is priced like any other extra placement (ladder + size tier).
  // On the varsity templates that came to $0 against a "+$19" badge; as a
  // 4th or 5th spot on person-portrait/rk-special-date it came to $25/$35
  // against the same badge. Quote the real number instead of a decorative
  // one, and say nothing when the answer is "no extra charge".
  const sleeveDefaultMm = sleevePlacement.maxWidthMm ?? 60;
  const sleeveCostCents =
    additionalPlacementFeeFor(config, spots.length + 1) +
    tierForSize(
      sleeveDefaultMm,
      effectiveTiersFor(sleevePlacement, config),
      true,
    ).feeCents;

  const toggleEmoji = (id: string) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    // Spawn a new text draft on the sleeve placement, prefilled with
    // whatever name the customer typed in this card. They land in the
    // editor to pick a font + thread colour. Emoji extras stack into
    // pricing.ts via extraAddonIds.
    if (!name.trim()) return;
    const fresh = emptyDraft();
    fresh.placementSlug = sleevePlacement.slug;
    fresh.mode = "text";
    fresh.text = name.trim();
    fresh.extraAddonIds = Array.from(ticked);
    // Audit 2026-10: emptyDraft seeds 80mm, which is wider than a sleeve
    // can take (l-sleeve caps at 60mm on the live config). This draft
    // never goes through pickPlacement (the slug is preset), so the
    // order line carried an impossible 80mm sleeve. Seed from the
    // placement's own default, never above its cap. PlacementSection's
    // clamp effect is the backstop for every other preset-slug draft.
    const sleeveCap =
      typeof sleevePlacement.maxWidthMm === "number" &&
      sleevePlacement.maxWidthMm > 0
        ? sleevePlacement.maxWidthMm
        : 60;
    fresh.sizeMm = Math.min(sleevePlacement.defaultWidthMm || 60, sleeveCap);
    onPickSleeve(fresh);
    setTicked(new Set());
    setName("");
  };

  return (
    <section className="rk-pc-sec is-active rk-pc-sleeve-cross-sell">
      <div className="rk-pc-sec__head" role="heading" aria-level={2}>
        <span
          className="rk-pc-sec__num"
          style={
            sleeveUpsell.accent
              ? { background: sleeveUpsell.accent, color: "#fff" }
              : undefined
          }
        >
          +
        </span>
        <span className="rk-pc-sec__heading">
          <span className="rk-pc-sec__title">{sleeveUpsell.label}</span>
          <span className="rk-pc-sec__summary">
            {sleeveUpsell.hint ?? "Add a subtle personal touch on the sleeve."}
          </span>
        </span>
        <span className="rk-pc-sec__price">
          {sleeveCostCents > 0
            ? `+$${(sleeveCostCents / 100).toFixed(0)}`
            : "Included"}
        </span>
      </div>
      <div className="rk-pc-sec__body">
        <div className="rk-pc-sleeve-name">
          <label className="rk-pc-sleeve-name__label" htmlFor="rk-pc-sleeve-name-input">
            {hasKidSpot
              ? "Their name + age (e.g. \"Bella, 7\")"
              : "Name to stitch on the sleeve"}
          </label>
          <input
            id="rk-pc-sleeve-name-input"
            type="text"
            className="rk-pc-sleeve-name__input"
            placeholder={hasKidSpot ? "Bella, 7" : "Their name"}
            value={name}
            maxLength={24}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="rk-pc-sleeve-name__hint">
            You'll pick the font + thread colour on the next step.
          </span>
        </div>
        {emojiUpsells.length > 0 && (
          <div className="rk-pc-sleeve-emojis">
            <span className="rk-pc-sleeve-emojis__label">
              Add an emoji after the name (optional)
            </span>
            <div className="rk-pc-sleeve-emojis__grid">
              {emojiUpsells.map((u) => {
                const isOn = ticked.has(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={`rk-pc-sleeve-emojis__chip${isOn ? " is-selected" : ""}`}
                    onClick={() => toggleEmoji(u.id)}
                  >
                    <span className="rk-pc-sleeve-emojis__glyph">
                      {u.appendGlyph ?? "✦"}
                    </span>
                    <span className="rk-pc-sleeve-emojis__name">{u.label}</span>
                    <span className="rk-pc-sleeve-emojis__price">
                      +${(u.priceCents / 100).toFixed(0)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <button
          type="button"
          className="rk-pc-sleeve-cta"
          onClick={handleAdd}
          disabled={name.trim().length === 0}
        >
          {name.trim().length === 0
            ? "Type a name above to continue"
            : hasKidSpot
              ? `Add "${name.trim()}"${ticked.size > 0 ? ` + ${ticked.size} ${ticked.size === 1 ? "emoji" : "emojis"}` : ""}`
              : ticked.size > 0
                ? `Add "${name.trim()}" + ${ticked.size} ${ticked.size === 1 ? "emoji" : "emojis"}`
                : `Add "${name.trim()}" to my order`}
        </button>
      </div>
    </section>
  );
}

// Helper so App.tsx can ask "do we still need this section?" without
// re-implementing the gate. Honours appliesAtPlacementSlug when set
// (Phase 56) so the cross-sell targets the configured placement on
// non-tee garments.
export function shouldShowSleeveCrossSell(config: Config, spots: Spot[]): boolean {
  // Phase 70: resolve via the FlowBlueprint helper (falls back to
  // magic-kind lookup during migration).
  const sleeveUpsell: Upsell | null = sleeveCrossSellUpsellFor(config);
  if (!sleeveUpsell) return false;
  const explicitSlug =
    (sleeveUpsell as { appliesAtPlacementSlug?: string | null })
      .appliesAtPlacementSlug ?? null;
  // Phase 59: respect garment anatomy. Tote / sock / hat / beanie have
  // hasSleeves=false, so the sleeve cross-sell is irrelevant unless the
  // upsell has been explicitly redirected to a different slug.
  const garmentHasSleeves =
    (config.garmentType as { hasSleeves?: boolean }).hasSleeves !== false;
  if (!garmentHasSleeves && !explicitSlug) return false;
  const targetPlacement = explicitSlug
    ? config.placements.find((p) => p.slug === explicitSlug)
    : config.placements.find((p) => SLEEVE_SLUGS.has(p.slug));
  if (!targetPlacement) return false;
  if (spots.length === 0) return false;
  if (explicitSlug) {
    if (spots.some((s) => s.placementSlug === explicitSlug)) return false;
  } else {
    if (spots.some((s) => s.placementSlug && SLEEVE_SLUGS.has(s.placementSlug))) return false;
  }
  return true;
}
